import { DomainsConfig } from 'next-intl/routing';
import { applyQaVMHack } from '@/utility/applyQAVMHack';

export const allLocales = [
  'en-US',
  'en-CA',
  'fr-CA',
  'en-AU',
  'de-DE',
  'de-AT',
  'de-CH',
  'it-CH',
  'fr-CH',
  'fr-FR',
  'fi-FI',
  'en-IE',
  'en-GB',
  'it-IT',
  'da-DK',
  'fr-BE',
  'nl-BE',
  'es-ES',
  'pl-PL',
  'nl-NL',
  'no-NO',
  'sv-SE',
] as const;

/**
 * The canonical locale format used throughout the app (BCP-47, as next-intl
 * emits it). Convert to a wire format only at the request boundary — see
 * `getApiLocale` / `getSprdLocale` in `@/utility/locale`.
 *
 * Derived from `allLocales` rather than `locales` so the type is independent of
 * the `PLATFORM` split below.
 */
export type Locale = (typeof allLocales)[number];

export const locales: ReadonlyArray<Locale> =
  process.env.PLATFORM === 'NA'
    ? ['en-US', 'en-CA', 'fr-CA', 'en-AU']
    : [
        'de-DE',
        'de-AT',
        'de-CH',
        'it-CH',
        'fr-CH',
        'fr-FR',
        'fi-FI',
        'en-IE',
        'en-GB',
        'it-IT',
        'da-DK',
        'fr-BE',
        'nl-BE',
        'es-ES',
        'pl-PL',
        'nl-NL',
        'no-NO',
        'sv-SE',
      ];

// Complete TLD map for every shop locale. `locales` / `domains` stay
// platform-scoped so this deployment does not start serving the other
// platform, but country switching still needs the opposite shop's host.
export const topLevelDomainMap: Record<Locale, string> = {
  'en-US': 'com',
  'en-CA': 'ca',
  'fr-CA': 'ca',
  'en-AU': 'com.au',
  'de-DE': 'de',
  'de-AT': 'at',
  'de-CH': 'ch',
  'it-CH': 'ch',
  'fr-CH': 'ch',
  'fr-FR': 'fr',
  'fi-FI': 'fi',
  'en-IE': 'ie',
  'en-GB': 'co.uk',
  'it-IT': 'it',
  'da-DK': 'dk',
  'fr-BE': 'be',
  'nl-BE': 'be',
  'es-ES': 'es',
  'pl-PL': 'pl',
  'nl-NL': 'nl',
  'no-NO': 'no',
  'sv-SE': 'se',
};

const getShopHostParts = () => {
  if (typeof window !== 'undefined') {
    const parts = window.location.hostname.split('.');
    return { subdomain: parts[0], hostname: parts[1] };
  }
  return { subdomain: process.env.SUBDOMAIN, hostname: process.env.HOSTNAME };
};

export const getShopDomainForLocale = (locale: Locale): string | undefined => {
  const tld = topLevelDomainMap[locale];
  if (!tld) return undefined;

  const { subdomain, hostname } = getShopHostParts();
  if (!subdomain || !hostname) return undefined;

  return `${subdomain}.${hostname}.${applyQaVMHack(tld)}`;
};

export const domains: DomainsConfig<ReadonlyArray<Locale>> =
  process.env.PLATFORM === 'NA'
    ? [
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('com')}`,
          locales: ['en-US'],
          defaultLocale: 'en-US',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('ca')}`,
          locales: ['en-CA', 'fr-CA'],
          defaultLocale: 'en-CA',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('com.au')}`,
          locales: ['en-AU'],
          defaultLocale: 'en-AU',
        },
      ]
    : [
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('de')}`,
          locales: ['de-DE'],
          defaultLocale: 'de-DE',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('ch')}`,
          locales: ['de-CH', 'fr-CH', 'it-CH'],
          defaultLocale: 'de-CH',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('at')}`,
          locales: ['de-AT'],
          defaultLocale: 'de-AT',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('fr')}`,
          locales: ['fr-FR'],
          defaultLocale: 'fr-FR',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('fi')}`,
          locales: ['fi-FI'],
          defaultLocale: 'fi-FI',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('ie')}`,
          locales: ['en-IE'],
          defaultLocale: 'en-IE',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('co.uk')}`,
          locales: ['en-GB'],
          defaultLocale: 'en-GB',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('it')}`,
          locales: ['it-IT'],
          defaultLocale: 'it-IT',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('dk')}`,
          locales: ['da-DK'],
          defaultLocale: 'da-DK',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('be')}`,
          locales: ['fr-BE', 'nl-BE'],
          defaultLocale: 'fr-BE',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('es')}`,
          locales: ['es-ES'],
          defaultLocale: 'es-ES',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('pl')}`,
          locales: ['pl-PL'],
          defaultLocale: 'pl-PL',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('no')}`,
          locales: ['no-NO'],
          defaultLocale: 'no-NO',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('se')}`,
          locales: ['sv-SE'],
          defaultLocale: 'sv-SE',
        },
        {
          domain: `${process.env.SUBDOMAIN}.${process.env.HOSTNAME}.${applyQaVMHack('nl')}`,
          locales: ['nl-NL'],
          defaultLocale: 'nl-NL',
        },
      ];

export const localePrefixes: Partial<Record<Locale, string>> =
  process.env.PLATFORM === 'NA'
    ? {
        'fr-CA': '/fr',
      }
    : {
        'nl-BE': '/nl',
        'it-CH': '/it',
        'fr-CH': '/fr',
        'fr-CA': '/fr',
      };

/**
 * Narrows an untrusted `[locale]` route param to a locale this deployment
 * actually serves.
 *
 * Every segment that reads `params.locale` has to check this for itself: React
 * renders a layout's children in parallel with the layout, so a `notFound()` in
 * a parent layout does not stop the subtree below it from rendering with a
 * bogus locale.
 */
export const isLocale = (value: string): value is Locale =>
  (locales as ReadonlyArray<string>).includes(value);

export const getDynamicRoutingConfig = () => {
  if (typeof window === 'undefined') {
    return domains;
  } else {
    return window.__runtimeDomains;
  }
};
